import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  FaChevronLeft,
  FaChevronRight,
  FaVolumeUp,
  FaVolumeMute,
  FaTimes,
  FaExpand,
} from "react-icons/fa";
import "./MediaGallery.css";

/**
 * MediaGallery Component
 *
 * A reusable full-width media gallery with image/video support, navigation,
 * and fallback logic for different content types.
 *
 * @param {Object} props
 * @param {string[]} props.galleryUrls - Array of image/video URLs to display
 * @param {string} props.fallbackImage - Primary fallback image if gallery is empty
 * @param {string} props.secondaryFallback - Secondary fallback if primary is not available
 * @param {string} props.tertiaryFallback - Tertiary fallback if secondary is not available
 * @param {string} props.alt - Alt text for images
 * @param {React.ReactNode} props.overlayContent - Content to overlay on the gallery
 */
export default function MediaGallery({
  galleryUrls = [],
  fallbackImage = null,
  secondaryFallback = null,
  tertiaryFallback = null,
  alt = "Gallery image",
  overlayContent = null,
}) {
  // Gallery state
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isVideoMuted, setIsVideoMuted] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [failedUrls, setFailedUrls] = useState(new Set());
  const [videoEnded, setVideoEnded] = useState(false);

  // Ref for the inline video element - avoids remounting on fullscreen toggle
  const videoRef = useRef(null);

  // Touch/swipe state
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [touchHandled, setTouchHandled] = useState(false);
  const [muteButtonTouched, setMuteButtonTouched] = useState(false);

  // Helper function to check if URL is a video
  const isVideoUrl = (url) => {
    if (!url) return false;
    const videoExtensions = ['.mp4', '.webm', '.mov', '.avi', '.ogg'];
    const lowerUrl = url.toLowerCase();
    return videoExtensions.some(ext => lowerUrl.includes(ext));
  };

  // Determine media URLs to display (gallery or fallbacks)
  const mediaUrls = useMemo(() => {
    // If gallery has URLs, use them
    if (galleryUrls && galleryUrls.length > 0) {
      // Sort to put videos first
      const urls = [...galleryUrls];
      return urls.sort((a, b) => {
        const aIsVideo = isVideoUrl(a);
        const bIsVideo = isVideoUrl(b);
        if (aIsVideo && !bIsVideo) return -1;
        if (!aIsVideo && bIsVideo) return 1;
        return 0;
      });
    }

    // Otherwise use fallbacks in order of priority
    const fallbacks = [fallbackImage, secondaryFallback, tertiaryFallback].filter(Boolean);
    return fallbacks;
  }, [galleryUrls, fallbackImage, secondaryFallback, tertiaryFallback]);

  // Filter out failed URLs to prevent infinite retry loops
  const activeMediaUrls = useMemo(() => {
    if (failedUrls.size === 0) return mediaUrls;
    return mediaUrls.filter(url => !failedUrls.has(url));
  }, [mediaUrls, failedUrls]);

  // Check if gallery has any videos
  const hasVideo = useMemo(() => {
    return activeMediaUrls.some(url => isVideoUrl(url));
  }, [activeMediaUrls]);

  // Clamp currentIndex when activeMediaUrls shrinks
  useEffect(() => {
    if (activeMediaUrls.length > 0 && currentIndex >= activeMediaUrls.length) {
      setCurrentIndex(activeMediaUrls.length - 1);
    }
  }, [activeMediaUrls.length, currentIndex]);

  // Reset videoEnded when the displayed URL changes (navigation or new page).
  // Use the URL string value (not the array reference) so React memo cache evictions
  // in production don't incorrectly reset this state and trigger re-downloads.
  useEffect(() => {
    setVideoEnded(false);
  }, [activeMediaUrls[currentIndex], currentIndex]);


  // Handle media load errors - stops browser from retrying
  const handleMediaError = useCallback((e, url) => {
    // Remove src to stop the browser from continuously retrying the fetch
    if (e.currentTarget.tagName === 'VIDEO') {
      e.currentTarget.removeAttribute('src');
      e.currentTarget.load();
    }
    setFailedUrls(prev => {
      const next = new Set(prev);
      next.add(url);
      return next;
    });
  }, []);

  // Auto-rotate gallery images (but not if there's a video)
  useEffect(() => {
    // Only auto-rotate if there are multiple images, not paused, and no video
    if (!activeMediaUrls || activeMediaUrls.length <= 1 || isPaused || hasVideo) {
      return;
    }

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) =>
        (prevIndex + 1) % activeMediaUrls.length
      );
    }, 4000); // Rotate every 4 seconds

    return () => clearInterval(interval);
  }, [activeMediaUrls, isPaused, hasVideo]);

  // Touch handlers for gallery swipe
  const minSwipeDistance = 50; // Minimum distance for a swipe

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart) return;

    const distance = touchEnd ? touchStart - touchEnd : 0;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    // Check if this was a tap (no movement or minimal movement)
    const isTap = !touchEnd || Math.abs(distance) < 10;

    // Reset touch state
    setTouchStart(null);
    setTouchEnd(null);

    if (isTap && !isFullscreen) {
      // This is a tap in normal view, trigger fullscreen
      setIsFullscreen(prev => !prev);
      // Set flag to prevent subsequent click event
      setTouchHandled(true);
      setTimeout(() => setTouchHandled(false), 500);
      return;
    }

    if (activeMediaUrls && activeMediaUrls.length > 1) {
      if (isLeftSwipe) {
        // Swipe left - go to next image
        setCurrentIndex((currentIndex + 1) % activeMediaUrls.length);
      } else if (isRightSwipe) {
        // Swipe right - go to previous image
        setCurrentIndex((currentIndex - 1 + activeMediaUrls.length) % activeMediaUrls.length);
      }
    }
  };

  // Navigation handlers
  const goToPrevious = () => {
    setIsPaused(true);
    setCurrentIndex(
      (currentIndex - 1 + activeMediaUrls.length) % activeMediaUrls.length
    );
  };

  const goToNext = () => {
    setIsPaused(true);
    setCurrentIndex((currentIndex + 1) % activeMediaUrls.length);
  };

  const goToIndex = (index) => {
    setIsPaused(true);
    setCurrentIndex(index);
  };

  const toggleFullscreen = () => {
    // Ignore if touch was just handled
    if (touchHandled) return;

    // When entering fullscreen, unmute video
    if (!isFullscreen && isVideoUrl(activeMediaUrls[currentIndex])) {
      setIsVideoMuted(false);
    }

    setIsFullscreen(!isFullscreen);
  };

  const closeFullscreen = () => {
    // When exiting fullscreen, mute video
    if (isVideoUrl(activeMediaUrls[currentIndex])) {
      setIsVideoMuted(true);
    }

    setIsFullscreen(false);
  };

  // Pause inline video when fullscreen opens (video stays mounted, just hidden)
  // Resume when fullscreen closes so it picks up without re-downloading
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (isFullscreen) {
      video.pause();
    } else {
      video.play().catch(() => {});
    }
  }, [isFullscreen]);

  // When inline video ends, clear src via React state so the browser releases
  // the buffer. Directly mutating the DOM attribute (removeAttribute + load)
  // was causing an infinite re-fetch loop: React would restore src={currentUrl}
  // on the next render, restarting the download every time the video ended.
  const handleVideoEnded = useCallback(() => {
    setVideoEnded(true);
  }, []);

  // Don't render if no media available
  if (!activeMediaUrls || activeMediaUrls.length === 0) {
    return null;
  }

  const currentUrl = activeMediaUrls[currentIndex];
  const isCurrentVideo = isVideoUrl(currentUrl);

  // Poster image shown before video loads and after it ends
  const videoPoster = fallbackImage || secondaryFallback || tertiaryFallback || undefined;

  return (
    <>
      <div className={`media-gallery-wrapper ${activeMediaUrls.length === 1 ? 'single-media' : ''}`}>
        <div className="media-gallery-section">
          {/* Media Container */}
          <div className="media-gallery-container">
            <div
              className="media-gallery-viewer"
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
              style={{ cursor: 'pointer' }}
            >
            {isCurrentVideo ? (
              <>
                {/* Video stays mounted at all times — never unmounts on fullscreen toggle.
                    Hidden via CSS when fullscreen is open to avoid two simultaneous
                    video instances fetching the same file. Buffer is preserved so
                    it resumes without re-downloading when fullscreen closes. */}
                <video
                  ref={videoRef}
                  src={videoEnded ? undefined : currentUrl}
                  className="gallery-media"
                  style={{ display: isFullscreen ? 'none' : undefined }}
                  autoPlay
                  muted={isVideoMuted}
                  playsInline
                  preload="metadata"
                  poster={videoPoster}
                  onClick={toggleFullscreen}
                  onEnded={handleVideoEnded}
                  onError={(e) => handleMediaError(e, currentUrl)}
                />
                {!isFullscreen && (
                  <button
                    className="video-mute-toggle"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!muteButtonTouched) {
                        setIsVideoMuted(!isVideoMuted);
                      }
                    }}
                    onTouchStart={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setMuteButtonTouched(true);
                    }}
                    onTouchEnd={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setIsVideoMuted(!isVideoMuted);
                      setTimeout(() => setMuteButtonTouched(false), 300);
                    }}
                    aria-label={isVideoMuted ? "Unmute video" : "Mute video"}
                  >
                    {isVideoMuted ? <FaVolumeMute /> : <FaVolumeUp />}
                  </button>
                )}
              </>
            ) : (
              <img
                key={currentUrl}
                src={currentUrl}
                alt={`${alt} ${currentIndex + 1}`}
                className="gallery-media"
                loading="lazy"
                decoding="async"
                onClick={toggleFullscreen}
                onError={(e) => handleMediaError(e, currentUrl)}
              />
            )}

            {/* Fullscreen toggle button - only show when not in fullscreen */}
            {!isFullscreen && (
              <button
                className="gallery-fullscreen-toggle"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFullscreen();
                }}
                onTouchStart={(e) => e.stopPropagation()}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  toggleFullscreen();
                }}
                aria-label="Enter fullscreen"
              >
                <FaExpand />
              </button>
            )}
          </div>
          <div className="media-gallery-overlay"></div>

          {/* Overlay Content (e.g., title, badges, etc.) */}
          {overlayContent && !isFullscreen && (
            <div className="media-gallery-content">
              {overlayContent}
            </div>
          )}
        </div>
        </div>

        {/* Media counter and navigation - outside and below gallery */}
        {activeMediaUrls.length > 1 && (
          <div className="media-counter-container">
            <button
              className="media-counter-nav"
              onClick={(e) => {
                e.stopPropagation();
                goToPrevious();
              }}
              onTouchStart={(e) => e.stopPropagation()}
              onTouchEnd={(e) => {
                e.stopPropagation();
                goToPrevious();
              }}
              disabled={currentIndex === 0}
              aria-label="Previous media"
            >
              <FaChevronLeft />
            </button>
            <span className="media-counter-text">
              {currentIndex + 1} / {activeMediaUrls.length}
            </span>
            <button
              className="media-counter-nav"
              onClick={(e) => {
                e.stopPropagation();
                goToNext();
              }}
              onTouchStart={(e) => e.stopPropagation()}
              onTouchEnd={(e) => {
                e.stopPropagation();
                goToNext();
              }}
              disabled={currentIndex === activeMediaUrls.length - 1}
              aria-label="Next media"
            >
              <FaChevronRight />
            </button>
          </div>
        )}
      </div>

    {/* Fullscreen Modal */}
    {isFullscreen && (
      <div className="gallery-fullscreen-modal">
        <button
          className="gallery-fullscreen-close"
          onClick={closeFullscreen}
          aria-label="Close fullscreen"
        >
          <FaTimes />
        </button>

        <div
          className="gallery-fullscreen-content"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {isCurrentVideo ? (
            <>
              <video
                key={`fs-${currentUrl}`}
                src={currentUrl}
                className="gallery-fullscreen-media"
                autoPlay
                loop
                muted={isVideoMuted}
                playsInline
                controls
                onError={(e) => handleMediaError(e, currentUrl)}
              />
            </>
          ) : (
            <img
              src={currentUrl}
              alt={`${alt} ${currentIndex + 1}`}
              className="gallery-fullscreen-media"
            />
          )}

          {/* Media counter in fullscreen */}
          {activeMediaUrls.length > 1 && (
            <div className="media-counter-container fullscreen-counter">
              <button
                className="media-counter-nav"
                onClick={(e) => {
                  e.stopPropagation();
                  goToPrevious();
                }}
                disabled={currentIndex === 0}
                aria-label="Previous media"
              >
                <FaChevronLeft />
              </button>
              <span className="media-counter-text">
                {currentIndex + 1} / {activeMediaUrls.length}
              </span>
              <button
                className="media-counter-nav"
                onClick={(e) => {
                  e.stopPropagation();
                  goToNext();
                }}
                disabled={currentIndex === activeMediaUrls.length - 1}
                aria-label="Next media"
              >
                <FaChevronRight />
              </button>
            </div>
          )}
        </div>
      </div>
    )}
    </>
  );
}
